import { Link as LinkIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { HostName, HOSTS } from 'pages/About/hosts';
import { EpisodeResourceGroup } from 'shared/data/episodes';
import { Heading } from 'shared/components/Heading';
import { SubscribeCard } from '../components/SubscribeCard';
import { ALLOW_SUBSCRIPTIONS } from 'shared/constants/common';

type EpisodeSidebarSectionProps = {
  resourceGroups?: EpisodeResourceGroup[];
  host: HostName
};

export function EpisodeSidebarSection({ resourceGroups, host }: EpisodeSidebarSectionProps) {
  const { t } = useTranslation('episodeDetail');
  const hostInfo = HOSTS.find((curHost) => curHost.name === host)

  const {name, shortBio, image} = hostInfo ? hostInfo : {name: '', shortBio: '', image: undefined}

  const linkedResourceGroups = (resourceGroups ?? [])
    .map((resourceGroup) => ({
      ...resourceGroup,
      resources: resourceGroup.resources.filter((resource) => resource.url),
    }))
    .filter((resourceGroup) => resourceGroup.resources.length > 0);

  return (
    <div className="lg:col-span-5 space-y-10 md:space-y-16">
      {linkedResourceGroups.length > 0 ? <div className="bg-surface-container-low p-10 rounded-3xl">
        <Heading variant="sub" as="h3" className="mb-8 flex items-center gap-3">
          <LinkIcon size={20} className="text-tertiary" />
          {t('sidebar.inThisEpisode')}
        </Heading>
        <div className="space-y-6">
          {linkedResourceGroups.map((resourceGroup) => (
            <ol key={resourceGroup.tag}>
              <span className="font-sans text-xs text-tertiary uppercase tracking-tighter block mb-1">
                {resourceGroup.tag}
              </span>
              {resourceGroup.resources.map((resource, index) => (
                <li key={index} className="pt-1">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <span className="text-on-surface group-hover:text-tertiary transition-colors underline underline-offset-4 decoration-outline-variant/30">
                      {resource.label}
                    </span>
                    <span className="sr-only">{t('common:a11y.opensInNewTab')}</span>
                  </a>
                </li>
              ))}
            </ol>
          ))}
        </div>
      </div> : null}

      <div className="flex items-center gap-6 p-6">
        <div className="w-20 aspect-square rounded-full overflow-hidden border-2 border-tertiary/20 p-1">
          <img src={image?.src} alt={image?.alt} className="w-full h-full rounded-full bg-surface-container-high object-cover" />
        </div>
        <div>
          <span className="font-sans text-xs uppercase tracking-widest text-outline">
            {t('sidebar.hostedBy')}
          </span>
          <Heading variant="sub">{name}</Heading>
          <p className="text-sm text-on-surface-variant italic">
            {shortBio}
          </p>
        </div>
      </div>

      {ALLOW_SUBSCRIPTIONS ? <SubscribeCard /> : null}
    </div>
  );
}
